"use client";
import { createLogAdapter } from "@/lib/logger/console-adapter";

import { Globe, TrendingUp } from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import MiniPriceChart from "@/components/shared/mini-price-chart";

import { useLanguage } from "@/hooks/useLanguage";

const uiLog = createLogAdapter("components/shared/hot-tokens-list.tsx");

// X (Twitter) Icon Component
const XIcon = ({ className }: { className?: string }) => (
	<svg viewBox="0 0 24 24" className={className} fill="currentColor">
		<path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
	</svg>
);

// Telegram Icon Component
const TelegramIcon = ({ className }: { className?: string }) => (
	<svg
		width="16"
		height="16"
		xmlns="http://www.w3.org/2000/svg"
		x="0px"
		y="0px"
		viewBox="0 0 50 50"
		className={className}
		fill="currentColor"
	>
		<path d="M 44.376953 5.9863281 C 43.889905 6.0076957 43.415817 6.1432497 42.988281 6.3144531 C 42.565113 6.4845113 40.128883 7.5243408 36.53125 9.0625 C 32.933617 10.600659 28.256963 12.603668 23.621094 14.589844 C 14.349356 18.562196 5.2382813 22.470703 5.2382812 22.470703 L 5.3046875 22.445312 C 5.3046875 22.445312 4.7547875 22.629122 4.1972656 23.017578 C 3.9185047 23.211806 3.6186028 23.462555 3.3730469 23.828125 C 3.127491 24.193695 2.9479735 24.711788 3.015625 25.259766 C 3.2532479 27.184511 5.2480469 27.730469 5.2480469 27.730469 L 5.2558594 27.734375 L 14.158203 30.78125 C 14.385177 31.538434 16.858319 39.792923 17.402344 41.541016 C 17.702797 42.507484 17.984013 43.064995 18.277344 43.445312 C 18.424133 43.635633 18.577962 43.782915 18.748047 43.890625 C 18.815627 43.933415 18.8867 43.965525 18.957031 43.994141 C 18.958531 43.994806 18.959437 43.99348 18.960938 43.994141 C 18.969579 43.997952 18.977708 43.998295 18.986328 44.001953 L 18.962891 43.996094 C 18.979231 44.002694 18.995359 44.013801 19.011719 44.019531 C 19.043456 44.030655 19.062905 44.030268 19.103516 44.039062 C 20.123059 44.395042 20.966797 43.734375 20.966797 43.734375 L 21.001953 43.707031 L 26.470703 38.634766 L 35.345703 45.554688 L 35.457031 45.605469 C 37.010484 46.295216 38.415349 45.910403 39.193359 45.277344 C 39.97137 44.644284 40.277344 43.828125 40.277344 43.828125 L 40.310547 43.742188 L 46.832031 9.7519531 C 46.998903 8.9915162 47.022612 8.334202 46.865234 7.7402344 C 46.707857 7.1462668 46.325492 6.6299361 45.845703 6.34375 C 45.365914 6.0575639 44.864001 5.9649605 44.376953 5.9863281 z M 44.429688 8.0195312 C 44.627491 8.0103707 44.774102 8.032983 44.820312 8.0605469 C 44.866523 8.0881109 44.887272 8.0844829 44.931641 8.2519531 C 44.976011 8.419423 45.000036 8.7721605 44.878906 9.3242188 L 44.875 9.3359375 L 38.390625 43.128906 C 38.375275 43.162926 38.240151 43.475531 37.931641 43.726562 C 37.616914 43.982653 37.266874 44.182554 36.337891 43.792969 L 26.632812 36.224609 L 26.359375 36.009766 L 26.353516 36.015625 L 23.451172 33.837891 L 39.761719 14.648438 A 1.0001 1.0001 0 0 0 38.974609 13 A 1.0001 1.0001 0 0 0 38.445312 13.167969 L 14.84375 28.902344 L 5.9277344 25.849609 C 5.9277344 25.849609 5.0423771 25.356927 5 25.013672 C 4.99765 24.994652 4.9871961 25.011869 5.0332031 24.943359 C 5.0792101 24.874869 5.1948546 24.759225 5.3398438 24.658203 C 5.6298218 24.456159 5.9609375 24.333984 5.9609375 24.333984 L 5.9941406 24.322266 L 6.0273438 24.308594 C 6.0273438 24.308594 15.138894 20.399882 24.410156 16.427734 C 29.045787 14.44166 33.721617 12.440122 37.318359 10.902344 C 40.914175 9.3649615 43.512419 8.2583658 43.732422 8.1699219 C 43.982886 8.0696253 44.231884 8.0286918 44.429688 8.0195312 z M 33.613281 18.792969 L 21.244141 33.345703 L 21.238281 33.351562 A 1.0001 1.0001 0 0 0 21.183594 33.423828 A 1.0001 1.0001 0 0 0 21.128906 33.507812 A 1.0001 1.0001 0 0 0 20.998047 33.892578 A 1.0001 1.0001 0 0 0 20.998047 33.900391 L 19.386719 41.146484 C 19.35993 41.068197 19.341173 41.039555 19.3125 40.947266 L 19.3125 40.945312 C 18.800713 39.30085 16.467362 31.5161 16.144531 30.439453 L 33.613281 18.792969 z M 22.640625 35.730469 L 24.863281 37.398438 L 21.597656 40.425781 L 22.640625 35.730469 z"></path>
	</svg>
);

interface Token {
	id: string;
	name: string;
	ticker: string;
	price: number;
	creator: string;
	created_time: string;
	holder_count: number;
	holder_dev: number;
	comment_count: number;
	volume_24: number;
	marketcap: number;
	price_5m: number;
	price_1h: number;
	price_6h: number;
	image: string;
	twitter: string;
	telegram: string;
	website: string;
	buy_count: number;
	sell_count: number;
	total_supply: number;
	source?: "5m" | "1h" | "6h"; // 添加数据源标记
}

interface ApiResponse {
	data: Token[];
	page: number;
	limit: number;
	count: number;
}

interface Trade {
	id: string;
	user: string;
	token: string;
	time: string;
	buy: boolean;
	amount_btc: number;
	amount_token: number;
	price: number;
	bonded: boolean;
	user_username: string;
	user_image: string | null;
	decimals: number;
	divisibility: number;
}

interface TradesResponse {
	data: Trade[];
	page: number;
	limit: number;
	count: number;
}

export default function HotTokensList() {
	const [hotTokens, setHotTokens] = useState<Token[]>([]);
	const [isInitialLoad, setIsInitialLoad] = useState(true);
	const [tradeHighlights, setTradeHighlights] = useState<
		Map<string, "buy" | "sell">
	>(new Map());
	const hotTokensRef = useRef<Token[]>([]);
	const isInitialLoadRef = useRef(true);
	const lastTradeId = useRef<string | null>(null);
	const hotTokensInFlightRef = useRef(false);
	const tradesInFlightRef = useRef(false);
	const tradesIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const { getTranslation } = useLanguage();
	const router = useRouter();

	// 创建闪烁效果
	const createFlashEffect = (tokenId: string, tradeType: "buy" | "sell") => {
		const highlights = new Map();
		highlights.set(tokenId, tradeType);
		setTradeHighlights(highlights);

		let flashCount = 0;
		const maxFlashes = 5;

		const flashInterval = setInterval(() => {
			flashCount++;

			if (flashCount >= maxFlashes) {
				clearInterval(flashInterval);
				setTradeHighlights(new Map());
			} else {
				// 每200ms切换一次状态
				setTradeHighlights((prevHighlights) => {
					if (prevHighlights.has(tokenId)) {
						return new Map(); // 清除高亮
					} else {
						const newHighlights = new Map();
						newHighlights.set(tokenId, tradeType);
						return newHighlights; // 恢复高亮
					}
				});
			}
		}, 200);
	};

	// 获取实时交易记录
	const fetchLatestTrades = async () => {
		if (tradesInFlightRef.current) return;
		tradesInFlightRef.current = true;
		try {
			const response = await fetch(
				"https://api.odin.fun/v1/trades?page=1&limit=5&sort=time%3Adesc",
			);
			if (!response.ok) return;

			const data: TradesResponse = await response.json();

			if (data.data && data.data.length > 0) {
				// 如果这是第一次获取，记录第一个交易ID
				if (!lastTradeId.current) {
					lastTradeId.current = data.data[0].id;
					return;
				}

				// 找出新的交易
				const newTrades = data.data.filter(
					(trade) => parseInt(trade.id) > parseInt(lastTradeId.current!),
				);

				if (newTrades.length > 0) {
					// 更新最新交易ID
					lastTradeId.current = newTrades[0].id;

					// 为每个匹配的代币创建闪烁效果
					newTrades.forEach((trade) => {
						// 检查这个代币是否在当前显示的热门代币列表中
						if (
							hotTokensRef.current.some((token) => token.id === trade.token)
						) {
							createFlashEffect(trade.token, trade.buy ? "buy" : "sell");
						}
					});
				}
			}
		} catch (error) {
			uiLog.error("Error fetching trades:", error);
		} finally {
			tradesInFlightRef.current = false;
		}
	};

	// 获取热门代币数据
	const fetchHotTokens = async () => {
		if (hotTokensInFlightRef.current) return;
		hotTokensInFlightRef.current = true;
		try {
			// 分别获取三个数据源
			const [response1, response2, response3] = await Promise.all([
				fetch(
					"https://api.odin.fun/v1/tokens?page=1&limit=10&sort=price_delta_5m%3Adesc",
				),
				fetch(
					"https://api.odin.fun/v1/tokens?page=1&limit=10&sort=price_delta_1h%3Adesc",
				),
				fetch(
					"https://api.odin.fun/v1/tokens?page=1&limit=5&sort=price_delta_6h%3Adesc",
				),
			]);

			const [data1, data2, data3] = await Promise.all([
				response1.json() as Promise<ApiResponse>,
				response2.json() as Promise<ApiResponse>,
				response3.json() as Promise<ApiResponse>,
			]);

			// 按照要求的排列方式组合数据
			const combinedTokens: Token[] = [];
			const maxLength = Math.max(
				data1.data?.length || 0,
				data2.data?.length || 0,
				data3.data?.length || 0,
			);

			for (let i = 0; i < maxLength; i++) {
				if (data1.data && data1.data[i]) {
					combinedTokens.push({ ...data1.data[i], source: "5m" });
				}
				if (data2.data && data2.data[i]) {
					combinedTokens.push({ ...data2.data[i], source: "1h" });
				}
				if (data3.data && data3.data[i]) {
					combinedTokens.push({ ...data3.data[i], source: "6h" });
				}
			}

			// 按照id去重
			const uniqueTokens = combinedTokens.filter(
				(token, index, self) =>
					index === self.findIndex((t) => t.id === token.id),
			);

			// 截取前9条记录（3x3网格）
			const finalTokens = uniqueTokens.slice(0, 9);

			setHotTokens(finalTokens);

			// 只在第一次成功获取数据后设置为非初始加载状态
			if (isInitialLoadRef.current) {
				setIsInitialLoad(false);
				isInitialLoadRef.current = false;
			}
		} catch (error) {
			uiLog.error("Error fetching hot tokens:", error);
			// 如果不是初始加载，出错时不清空现有数据
			if (isInitialLoadRef.current) {
				setHotTokens([]);
			}
		} finally {
			hotTokensInFlightRef.current = false;
		}
	};

	useEffect(() => {
		fetchHotTokens();
		// 每2秒刷新一次数据
		const interval = setInterval(fetchHotTokens, 2000);
		return () => clearInterval(interval);
	}, []);

	useEffect(() => {
		hotTokensRef.current = hotTokens;
	}, [hotTokens]);

	// 每秒获取最新交易记录
	useEffect(() => {
		// 确保代币列表已加载
		if (hotTokensRef.current.length === 0 || tradesIntervalRef.current) return;

		// 立即获取一次
		fetchLatestTrades();

		// 每秒获取一次
		tradesIntervalRef.current = setInterval(fetchLatestTrades, 1000);
	}, [hotTokens.length]);

	useEffect(() => {
		return () => {
			if (tradesIntervalRef.current) {
				clearInterval(tradesIntervalRef.current);
				tradesIntervalRef.current = null;
			}
		};
	}, []);

	const formatPrice = (price: number) => {
		const satsPrice = price / 1000;
		return `${satsPrice.toFixed(3)} Sats`;
	};

	const formatChange = (token: any) => {
		const currentPrice = token.price || 0;

		// 根据source选择对应的历史价格
		let historicalPrice = 0;
		let timeLabel = "";

		switch (token.source) {
			case "5m":
				historicalPrice = token.price_5m || 0;
				timeLabel = "5m";
				break;
			case "1h":
				historicalPrice = token.price_1h || 0;
				timeLabel = "1h";
				break;
			case "6h":
				historicalPrice = token.price_6h || 0;
				timeLabel = "6h";
				break;
			default:
				historicalPrice = token.price_1h || 0;
				timeLabel = "1h";
		}

		if (historicalPrice === 0 || currentPrice === 0) {
			return (
				<span className="flex items-center gap-1">
					<span className="text-[10px] text-gray-400">{timeLabel}</span>
					<span className="text-gray-500">0.00%</span>
				</span>
			);
		}

		const changePercent =
			((currentPrice - historicalPrice) / historicalPrice) * 100;
		const isPositive = changePercent >= 0;

		return (
			<span className="flex items-center gap-1">
				<span className="text-[10px] text-gray-400">{timeLabel}</span>
				<span className={isPositive ? "text-green-500" : "text-red-500"}>
					{isPositive ? "+" : ""}
					{changePercent.toFixed(2)}%
				</span>
			</span>
		);
	};

	const renderSocialIcons = (token: any) => {
		const socialLinks = [];

		if (token.twitter && token.twitter.trim() !== "") {
			socialLinks.push(
				<button
					key="twitter"
					onClick={(e) => {
						e.stopPropagation();
						window.open(token.twitter, "_blank");
					}}
					className="text-light-accent dark:text-dark-accent hover:text-light-accent/80 dark:hover:text-dark-accent/80 transition-colors"
					title="Twitter/X"
				>
					<XIcon className="h-3 w-3" />
				</button>,
			);
		}

		if (token.telegram && token.telegram.trim() !== "") {
			socialLinks.push(
				<button
					key="telegram"
					onClick={(e) => {
						e.stopPropagation();
						window.open(token.telegram, "_blank");
					}}
					className="text-light-accent dark:text-dark-accent hover:text-light-accent/80 dark:hover:text-dark-accent/80 transition-colors"
					title="Telegram"
				>
					<TelegramIcon className="h-3 w-3" />
				</button>,
			);
		}

		if (token.website && token.website.trim() !== "") {
			socialLinks.push(
				<button
					key="website"
					onClick={(e) => {
						e.stopPropagation();
						window.open(token.website, "_blank");
					}}
					className="text-light-accent dark:text-dark-accent hover:text-light-accent/80 dark:hover:text-dark-accent/80 transition-colors"
					title="Website"
				>
					<Globe className="h-3 w-3" />
				</button>,
			);
		}

		return socialLinks.length > 0 ? (
			<div className="flex items-center space-x-1 ml-1">{socialLinks}</div>
		) : null;
	};

	const formatCreatedTime = (createdTime: string) => {
		try {
			const created = new Date(createdTime);
			const now = new Date();
			const diffMs = now.getTime() - created.getTime();
			const diffMinutes = Math.floor(diffMs / (1000 * 60));
			const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
			const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

			if (diffMinutes < 1) {
				return getTranslation("plaza.justNow");
			} else if (diffMinutes < 60) {
				return getTranslation("plaza.minutesAgo").replace(
					"{minutes}",
					String(diffMinutes),
				);
			} else if (diffHours < 24) {
				return getTranslation("plaza.hoursAgo").replace(
					"{hours}",
					String(diffHours),
				);
			} else {
				return getTranslation("plaza.daysAgo").replace(
					"{days}",
					String(diffDays),
				);
			}
		} catch {
			return createdTime;
		}
	};

	if (isInitialLoad && hotTokens.length === 0) {
		return (
			<div className="grid grid-cols-3 gap-3">
				{[...Array(9)].map((_, i) => (
					<div
						key={i}
						className="flex flex-col p-3 bg-gray-50 dark:bg-gray-800 rounded-lg animate-pulse"
					>
						<div className="flex items-center space-x-2 mb-2">
							<div className="w-8 h-8 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
							<div className="flex-1">
								<div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-2/3"></div>
							</div>
						</div>
						<div className="space-y-1">
							<div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-full"></div>
							<div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-3/4"></div>
							<div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-1/2"></div>
						</div>
					</div>
				))}
			</div>
		);
	}

	if (!hotTokens || hotTokens.length === 0) {
		return (
			<div className="text-center text-gray-500 dark:text-gray-400 py-8">
				{getTranslation("plaza.noHotTokens")}
			</div>
		);
	}

	return (
		<div className="grid grid-cols-3 gap-3">
			{hotTokens.map((token) => {
				const holderCount = token.holder_count || 0;
				const holderDev = token.holder_dev || 0;
				const totalSupply = 21000000 * 1e11;
				let devPercentage = "dev:rug";
				if (holderDev > 0) {
					const percentage = (holderDev / totalSupply) * 100;
					devPercentage = `dev:${percentage.toFixed(2)}%`;
				}

				const tradeType = tradeHighlights.get(token.id);
				const isHighlighted = !!tradeType;

				// 检测暗色模式
				const isDarkMode =
					typeof window !== "undefined" &&
					document.documentElement.classList.contains("dark");

				// 调试日志
				if (isHighlighted) {
					uiLog.info(`代币 ${token.ticker} 闪烁`, {
						tradeType,
						isDarkMode,
						animationName:
							tradeType === "buy"
								? isDarkMode
									? "flash-blue-dark"
									: "flash-blue"
								: isDarkMode
									? "flash-red-dark"
									: "flash-red",
					});
				}

				return (
					<div
						key={token.id}
						className={`flex flex-col p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer relative overflow-hidden ${
							!isHighlighted ? "bg-gray-50 dark:bg-gray-800" : ""
						}`}
						style={
							isHighlighted
								? {
										animation: "shake 0.5s ease-in-out",
										backgroundColor:
											tradeType === "buy" ? "#3b82f6" : "#ef4444",
										color: "white",
										transition: "background-color 0.2s ease-in-out",
									}
								: {}
						}
						onClick={() => (window.location.href = `/plaza/token/${token.id}`)}
					>
						{/* 移动端简化布局 */}
						<div className="block lg:hidden">
							{/* 第一行：图标 + Ticker */}
							<div className="flex items-center space-x-2 mb-1">
								<Image
									src={`https://images.odin.fun/token/${token.id}`}
									alt={token.ticker}
									width={20}
									height={20}
									className="w-5 h-5 rounded-full object-cover flex-shrink-0"
									onError={(e) => {
										e.currentTarget.src = "/placeholder.svg?height=20&width=20";
									}}
								/>
								<p className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">
									{token.ticker}
								</p>
							</div>

							{/* 第二行：价格 */}
							<div className="mb-1">
								<p className="text-xs font-medium text-gray-900 dark:text-gray-100">
									{formatPrice(token.price)}
								</p>
							</div>

							{/* 第三行：涨跌幅 */}
							<div className="mb-1">
								<div className="text-xs">{formatChange(token)}</div>
							</div>

							{/* 第四行：迷你曲线图 */}
							<div>
								<MiniPriceChart tokenId={token.id} width={60} height={20} />
							</div>
						</div>

						{/* 桌面端完整布局 */}
						<div className="hidden lg:block">
							<div className="flex items-center space-x-2 mb-2">
								{/* 代币图标 */}
								<Image
									src={`https://images.odin.fun/token/${token.id}`}
									alt={token.ticker}
									width={32}
									height={32}
									className="w-8 h-8 rounded-full object-cover flex-shrink-0"
									onError={(e) => {
										e.currentTarget.src = "/placeholder.svg?height=32&width=32";
									}}
								/>

								{/* Ticker和社交图标 */}
								<div className="flex-1 min-w-0">
									<div className="flex items-center justify-between">
										<div className="flex items-center">
											<p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
												{token.ticker}
											</p>
											{renderSocialIcons(token)}
										</div>
										{/* 迷你曲线图 */}
										<div className="ml-2">
											<MiniPriceChart
												tokenId={token.id}
												width={50}
												height={20}
											/>
										</div>
									</div>
								</div>
							</div>

							{/* 价格信息 */}
							<div className="space-y-1">
								<div className="flex items-center justify-between">
									<p className="text-sm font-medium text-gray-900 dark:text-gray-100">
										{formatPrice(token.price)}
									</p>
									<p className="text-xs">{formatChange(token)}</p>
								</div>

								{/* 交易信息第一行 */}
									<div className="text-xs text-gray-500 dark:text-gray-400">
										<span>{getTranslation("trades") || "交易"}:</span>{" "}
										<span className="text-gray-900 dark:text-gray-100">
											{(token.buy_count || 0) + (token.sell_count || 0)}
									</span>{" "}
									(
									<span className="text-green-500">
										{getTranslation("buy") || "买"}
										<span className="text-gray-900 dark:text-gray-100">
											{token.buy_count || 0}
										</span>
									</span>
									/
									<span className="text-red-500">
										{getTranslation("sell") || "卖"}
										<span className="text-gray-900 dark:text-gray-100">
											{token.sell_count || 0}
										</span>
									</span>
									)
								</div>

								{/* holders和dev信息第二行 */}
									<div className="text-xs text-gray-500 dark:text-gray-400">
										<span>{getTranslation("plaza.holders")}:</span>{" "}
										<span className="text-gray-900 dark:text-gray-100">
											{holderCount}
										</span>
									,{" "}
									<span className="text-gray-900 dark:text-gray-100">
										{devPercentage}
									</span>
									, {formatCreatedTime(token.created_time)}
								</div>
							</div>
						</div>
					</div>
				);
			})}
		</div>
	);
}
